'use client';

import { useState, useMemo } from 'react';
import { ScoredTechnology } from '@/lib/tech-stack-recommender';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';

interface HeadToHeadProps {
  availableTechnologies: ScoredTechnology[];
  initialSelection?: string[]; // IDs of pre-selected technologies
  maxSelections?: number; // Default 3
}

const COLORS = ['#E89841', '#4A90E2', '#50C878', '#9B59B6', '#E74C3C'];

// Category display names and groupings
const CATEGORY_GROUPS: Record<string, { name: string; icon: string; categories: string[] }> = {
  'data': {
    name: 'Data & Storage',
    icon: '🗄️',
    categories: ['database', 'cache', 'search', 'object-storage', 'data-warehouse', 'data-transformation'],
  },
  'backend': {
    name: 'Backend & APIs',
    icon: '⚙️',
    categories: ['backend-framework', 'api-gateway', 'graphql', 'message-queue', 'background-jobs', 'orm'],
  },
  'frontend': {
    name: 'Frontend & UI',
    icon: '🎨',
    categories: ['frontend-framework', 'meta-framework', 'state-management', 'ui-library', 'static-site-generator'],
  },
  'infrastructure': {
    name: 'Infrastructure & DevOps',
    icon: '🏗️',
    categories: ['cloud-provider', 'container-orchestration', 'ci-cd', 'infrastructure-as-code', 'cdn', 'service-mesh'],
  },
  'observability': {
    name: 'Observability & Quality',
    icon: '📊',
    categories: ['monitoring', 'observability', 'logging', 'error-tracking', 'testing', 'code-quality', 'analytics'],
  },
  'platform': {
    name: 'Platform & Services',
    icon: '🔌',
    categories: ['authentication', 'payments', 'email-service', 'headless-cms', 'communications', 'real-time', 'media-processing'],
  },
  'apps': {
    name: 'Apps & Desktop',
    icon: '📱',
    categories: ['mobile-framework', 'desktop-framework', 'documentation-platform'],
  },
  'tooling': {
    name: 'Build & Tooling',
    icon: '🔧',
    categories: ['build-tool', 'monorepo', 'secret-management', 'feature-flags', 'workflow-orchestration'],
  },
  'ai': {
    name: 'AI & Agents',
    icon: '🤖',
    categories: ['ai-agents'],
  },
};

// Get friendly category name
function getCategoryDisplayName(category: string): string {
  return category.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// Get group for a category
function getCategoryGroup(category: string): string {
  for (const [groupKey, group] of Object.entries(CATEGORY_GROUPS)) {
    if (group.categories.includes(category)) {
      return groupKey;
    }
  }
  return 'other';
}

export default function TechStackHeadToHead({
  availableTechnologies,
  initialSelection = [],
  maxSelections = 3,
}: HeadToHeadProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>(
    initialSelection.slice(0, maxSelections)
  );
  const [activeView, setActiveView] = useState<'overview' | 'technical' | 'operational' | 'cost' | 'compliance'>(
    'overview'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [activeGroupFilter, setActiveGroupFilter] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['data', 'backend', 'frontend']));

  const selectedTechnologies = useMemo(
    () => availableTechnologies.filter((tech) => selectedIds.includes(tech.id)),
    [availableTechnologies, selectedIds]
  );

  // Get the category of the first selected technology
  const selectedCategory = useMemo(() => {
    if (selectedIds.length === 0) return null;
    const firstTech = availableTechnologies.find((t) => t.id === selectedIds[0]);
    return firstTech?.category || null;
  }, [selectedIds, availableTechnologies]);

  // Filter available technologies based on selection, search, and group filter
  const filteredTechnologies = useMemo(() => {
    let filtered = availableTechnologies;

    // If a technology is selected, only show same category
    if (selectedCategory) {
      filtered = filtered.filter((tech) => tech.category === selectedCategory);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((tech) =>
        tech.name.toLowerCase().includes(query) ||
        tech.description.toLowerCase().includes(query) ||
        tech.category.toLowerCase().includes(query)
      );
    }

    // Apply group filter
    if (activeGroupFilter && !selectedCategory) {
      const groupCategories = CATEGORY_GROUPS[activeGroupFilter]?.categories || [];
      filtered = filtered.filter((tech) => groupCategories.includes(tech.category));
    }

    return filtered;
  }, [availableTechnologies, selectedCategory, searchQuery, activeGroupFilter]);

  // Group technologies by category
  const groupedTechnologies = useMemo(() => {
    const groups: Record<string, { groupKey: string; groupName: string; icon: string; categories: Record<string, ScoredTechnology[]> }> = {};

    filteredTechnologies.forEach((tech) => {
      const groupKey = getCategoryGroup(tech.category);
      const groupInfo = CATEGORY_GROUPS[groupKey] || { name: 'Other', icon: '📦', categories: [] };

      if (!groups[groupKey]) {
        groups[groupKey] = {
          groupKey,
          groupName: groupInfo.name,
          icon: groupInfo.icon,
          categories: {},
        };
      }

      if (!groups[groupKey].categories[tech.category]) {
        groups[groupKey].categories[tech.category] = [];
      }

      groups[groupKey].categories[tech.category].push(tech);
    });

    // Sort technologies within each category by score
    Object.values(groups).forEach((group) => {
      Object.values(group.categories).forEach((techs) => {
        techs.sort((a, b) => b.score - a.score);
      });
    });

    return groups;
  }, [filteredTechnologies]);

  // Get category counts for the filter tabs
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    availableTechnologies.forEach((tech) => {
      const groupKey = getCategoryGroup(tech.category);
      counts[groupKey] = (counts[groupKey] || 0) + 1;
    });
    return counts;
  }, [availableTechnologies]);

  // Popular technologies (top scored across all categories)
  const popularTechnologies = useMemo(() => {
    if (selectedCategory) return []; // Don't show when category is locked
    return [...availableTechnologies]
      .filter((t) => t.ecosystem.communitySize === 'very-large' || t.ecosystem.communitySize === 'large')
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);
  }, [availableTechnologies, selectedCategory]);

  const handleToggleSelection = (techId: string) => {
    if (selectedIds.includes(techId)) {
      setSelectedIds(selectedIds.filter((id) => id !== techId));
    } else if (selectedIds.length < maxSelections) {
      const tech = availableTechnologies.find((t) => t.id === techId);
      if (tech && (!selectedCategory || tech.category === selectedCategory)) {
        setSelectedIds([...selectedIds, techId]);
      }
    }
  };

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const decisionGuide = useMemo(
    () => generateDecisionGuide(selectedTechnologies),
    [selectedTechnologies]
  );

  const radarData = useMemo(
    () => getRadarChartData(selectedTechnologies),
    [selectedTechnologies]
  );

  const handleShare = () => {
    const url = `${window.location.origin}/tools/tech-stack-decision/compare?ids=${selectedIds.join(',')}`;
    navigator.clipboard.writeText(url);
    alert('Comparison link copied to clipboard!');
  };

  const clearFilters = () => {
    setSearchQuery('');
    setActiveGroupFilter(null);
    setSelectedIds([]);
  };

  return (
    <div className="space-y-8">
      {/* Technology Selector */}
      <div className="bg-surface rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Select Technologies to Compare</h2>
            <p className="text-sm text-foreground-secondary mt-1">
              Choose {maxSelections === 2 ? '2' : `up to ${maxSelections}`} technologies from the same category for detailed comparison
            </p>
          </div>
          <div className="text-sm text-foreground-secondary flex items-center gap-2">
            <span className="px-3 py-1 bg-accent-muted text-accent rounded-full font-semibold">
              {selectedIds.length}/{maxSelections}
            </span>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-6 space-y-4">
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search technologies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={!!selectedCategory}
              className="w-full px-4 py-3 pl-10 bg-background border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-tertiary hover:text-foreground"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Category Group Filter Tabs */}
          {!selectedCategory && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveGroupFilter(null)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  activeGroupFilter === null
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-background text-foreground-secondary hover:text-foreground hover:bg-background/70'
                }`}
              >
                All ({availableTechnologies.length})
              </button>
              {Object.entries(CATEGORY_GROUPS).map(([key, group]) => (
                categoryCounts[key] > 0 && (
                  <button
                    key={key}
                    onClick={() => setActiveGroupFilter(activeGroupFilter === key ? null : key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      activeGroupFilter === key
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-background text-foreground-secondary hover:text-foreground hover:bg-background/70'
                    }`}
                  >
                    {group.icon} {group.name} ({categoryCounts[key]})
                  </button>
                )
              ))}
            </div>
          )}
        </div>

        {/* Selected Category Lock Notice */}
        {selectedCategory && (
          <div className="mb-4 p-3 bg-accent-muted border border-border rounded-lg flex items-center justify-between">
            <p className="text-sm text-foreground/90">
              <span className="font-semibold">Comparing {getCategoryDisplayName(selectedCategory)}</span> — Only technologies in the same category can be compared.
            </p>
            <button
              onClick={clearFilters}
              className="px-3 py-1 bg-accent-muted text-accent rounded text-sm font-medium hover:bg-accent/30 transition-colors"
            >
              Clear & Start Over
            </button>
          </div>
        )}

        {/* Popular Technologies Quick Pick */}
        {popularTechnologies.length > 0 && !searchQuery && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-foreground-secondary mb-3 flex items-center gap-2">
              <span className="text-accent">⭐</span> Popular Technologies
            </h3>
            <div className="flex flex-wrap gap-2">
              {popularTechnologies.map((tech) => {
                const isSelected = selectedIds.includes(tech.id);
                const isDisabled = !isSelected && selectedIds.length >= maxSelections;
                const categoryLocked = selectedCategory && tech.category !== selectedCategory;

                return (
                  <button
                    key={tech.id}
                    onClick={() => !categoryLocked && handleToggleSelection(tech.id)}
                    disabled={isDisabled || !!categoryLocked}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                      isSelected
                        ? 'bg-accent text-accent-foreground'
                        : categoryLocked
                        ? 'bg-background/50 text-foreground-muted cursor-not-allowed'
                        : isDisabled
                        ? 'bg-background/50 text-foreground-tertiary cursor-not-allowed'
                        : 'bg-background text-foreground hover:bg-background/70 hover:text-accent'
                    }`}
                    title={categoryLocked ? 'Different category' : tech.description}
                  >
                    {tech.name}
                    <span className="text-xs opacity-70">{getCategoryDisplayName(tech.category)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Grouped Technologies */}
        <div className="space-y-4">
          {Object.entries(groupedTechnologies).map(([groupKey, group]) => (
            <div key={groupKey} className="border border-border-muted rounded-lg overflow-hidden">
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(groupKey)}
                className="w-full px-4 py-3 bg-background/50 flex items-center justify-between hover:bg-background/70 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">{group.icon}</span>
                  <span className="font-semibold text-foreground">{group.groupName}</span>
                  <span className="text-xs text-foreground-tertiary bg-cream/10 px-2 py-0.5 rounded-full">
                    {Object.values(group.categories).flat().length} technologies
                  </span>
                </div>
                <svg
                  className={`w-5 h-5 text-foreground-tertiary transition-transform ${expandedGroups.has(groupKey) ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Group Content */}
              {expandedGroups.has(groupKey) && (
                <div className="p-4 space-y-4">
                  {Object.entries(group.categories).map(([category, techs]) => (
                    <div key={category}>
                      {/* Category Header */}
                      <h4 className="text-xs font-semibold text-foreground-tertiary uppercase tracking-wider mb-2 flex items-center gap-2">
                        {getCategoryDisplayName(category)}
                        <span className="text-foreground-muted">({techs.length})</span>
                      </h4>

                      {/* Technology Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {techs.map((tech) => {
                          const isSelected = selectedIds.includes(tech.id);
                          const isDisabled = !isSelected && selectedIds.length >= maxSelections;
                          const categoryLocked = selectedCategory && tech.category !== selectedCategory;

                          return (
                            <button
                              key={tech.id}
                              onClick={() => !categoryLocked && handleToggleSelection(tech.id)}
                              disabled={isDisabled || !!categoryLocked}
                              className={`p-3 rounded-lg border text-left transition-all ${
                                isSelected
                                  ? 'border-accent bg-accent-muted'
                                  : categoryLocked
                                  ? 'border-border-muted bg-surface/30 opacity-40 cursor-not-allowed'
                                  : isDisabled
                                  ? 'border-border-muted bg-surface/50 opacity-50 cursor-not-allowed'
                                  : 'border-border hover:border-border-muted0 hover:bg-background/50'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-foreground text-sm truncate">{tech.name}</div>
                                  <p className="text-xs text-foreground-tertiary line-clamp-1 mt-0.5">{tech.description}</p>
                                </div>
                                <div className="flex flex-col items-end">
                                  <div className="text-sm font-bold text-accent">{tech.score.toFixed(0)}</div>
                                  {tech.ecosystem.popularityTrend === 'rapidly-growing' && (
                                    <span className="text-[10px] text-green-400">↑ trending</span>
                                  )}
                                </div>
                              </div>
                              {/* Quick stats */}
                              <div className="flex items-center gap-2 mt-2 text-[10px] text-foreground-tertiary">
                                <span className="capitalize">{tech.technical.maturity}</span>
                                <span>•</span>
                                <span className="capitalize">{tech.license}</span>
                                {tech.pricing.freeOption && (
                                  <>
                                    <span>•</span>
                                    <span className="text-green-400">Free tier</span>
                                  </>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredTechnologies.length === 0 && (
          <div className="text-center py-8">
            <p className="text-foreground-secondary">No technologies found matching your search.</p>
            <button
              onClick={clearFilters}
              className="mt-2 text-accent hover:underline text-sm"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Action Buttons */}
        {selectedIds.length > 0 && (
          <div className="mt-6 pt-4 border-t border-border-muted flex flex-wrap gap-2">
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-background text-foreground rounded-lg text-sm hover:bg-background/80 transition-colors"
            >
              Clear Selection
            </button>
            <button
              onClick={handleShare}
              className="px-4 py-2 bg-background text-foreground rounded-lg text-sm hover:bg-background/80 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share Comparison
            </button>
            {selectedIds.length >= 2 && (
              <span className="px-4 py-2 bg-green-500/20 text-green-400 rounded-lg text-sm font-medium">
                ✓ Ready to compare
              </span>
            )}
          </div>
        )}
      </div>

      {selectedTechnologies.length >= 2 && (
        <>
          {/* Radar Chart Visualization */}
          <div className="bg-surface rounded-lg p-6">
            <h3 className="text-xl font-bold text-foreground mb-4">Score Comparison Across All Dimensions</h3>
            <p className="text-sm text-foreground-secondary mb-6">
              Visual comparison of how each technology scores across the 6 evaluation dimensions
            </p>
            <ResponsiveContainer width="100%" height={400}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E8D5B7" strokeOpacity={0.2} />
                <PolarAngleAxis dataKey="dimension" tick={{ fill: '#E8D5B7', fontSize: 12 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#E8D5B7', fontSize: 10 }} />
                {selectedTechnologies.map((tech, index) => (
                  <Radar
                    key={tech.id}
                    name={tech.name}
                    dataKey={tech.id}
                    stroke={COLORS[index]}
                    fill={COLORS[index]}
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                ))}
                <Legend wrapperStyle={{ color: '#E8D5B7' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0A1E2E',
                    border: '1px solid #E8D5B7',
                    borderRadius: '8px',
                    color: '#E8D5B7',
                  }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* View Tabs */}
          <div className="flex flex-wrap gap-2">
            {['overview', 'technical', 'operational', 'cost', 'compliance'].map((view) => (
              <button
                key={view}
                onClick={() => setActiveView(view as typeof activeView)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition-colors ${
                  activeView === view
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-surface text-foreground hover:bg-surface/70'
                }`}
              >
                {view}
              </button>
            ))}
          </div>

          {/* Detailed Comparison Table */}
          <div className="bg-surface rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-foreground border-b border-border-muted w-48">
                      Criteria
                    </th>
                    {selectedTechnologies.map((tech, index) => (
                      <th
                        key={tech.id}
                        className="px-4 py-3 text-left text-sm font-semibold border-b border-border-muted"
                        style={{ color: COLORS[index] }}
                      >
                        {tech.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <ComparisonRows
                    view={activeView}
                    technologies={selectedTechnologies}
                    colors={COLORS}
                  />
                </tbody>
              </table>
            </div>
          </div>

          {/* Decision Guide */}
          <div className="bg-surface rounded-lg p-6">
            <h3 className="text-xl font-bold text-foreground mb-4">Decision Guide</h3>
            <p className="text-sm text-foreground-secondary mb-6">
              Based on the analysis, here&apos;s guidance on when to choose each technology
            </p>

            <div className="space-y-6">
              {decisionGuide.map((guide, index) => (
                <div
                  key={guide.technology}
                  className="border-l-4 pl-4"
                  style={{ borderColor: COLORS[index] }}
                >
                  <h4 className="font-bold text-foreground mb-3" style={{ color: COLORS[index] }}>
                    Choose {guide.technology} if:
                  </h4>
                  <ul className="space-y-2 mb-4">
                    {guide.when.map((reason, i) => (
                      <li key={i} className="text-sm text-foreground flex items-start">
                        <span className="text-accent mr-2">✓</span>
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>

                  {guide.reasons.length > 0 && (
                    <div className="mt-3 p-3 bg-background/50 rounded-lg">
                      <p className="text-xs font-semibold text-foreground-secondary mb-2">Key Strengths:</p>
                      <ul className="space-y-1">
                        {guide.reasons.map((reason, i) => (
                          <li key={i} className="text-sm text-foreground/90 flex items-start">
                            <span className="text-foreground-muted mr-2">•</span>
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Combined scenarios */}
            {selectedTechnologies.length === 2 && (
              <div className="mt-6 p-4 bg-accent-muted border border-border rounded-lg">
                <h4 className="font-bold text-accent mb-2">Consider Using Both If:</h4>
                <ul className="space-y-1">
                  <li className="text-sm text-foreground flex items-start">
                    <span className="text-accent mr-2">•</span>
                    <span>
                      Use {selectedTechnologies[0].name} for{' '}
                      {selectedTechnologies[0].suitability.useCases[0]?.toLowerCase()} and{' '}
                      {selectedTechnologies[1].name} for{' '}
                      {selectedTechnologies[1].suitability.useCases[0]?.toLowerCase()}
                    </span>
                  </li>
                  <li className="text-sm text-foreground flex items-start">
                    <span className="text-accent mr-2">•</span>
                    <span>
                      Different teams or projects have different requirements and constraints
                    </span>
                  </li>
                  <li className="text-sm text-foreground flex items-start">
                    <span className="text-accent mr-2">•</span>
                    <span>
                      You need the specific strengths of both technologies in different parts of your stack
                    </span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Print/Export Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => window.print()}
              className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors"
            >
              Print Comparison
            </button>
            <button
              onClick={() => exportToMarkdown(selectedTechnologies, decisionGuide)}
              className="px-6 py-3 bg-surface text-foreground rounded-lg font-semibold hover:bg-surface/70 transition-colors"
            >
              Export as Markdown
            </button>
          </div>
        </>
      )}

      {selectedTechnologies.length < 2 && selectedIds.length > 0 && (
        <div className="bg-surface rounded-lg p-8 text-center">
          <p className="text-foreground-secondary">
            Please select at least 2 technologies to see the comparison
          </p>
        </div>
      )}
    </div>
  );
}

function ComparisonRows({
  view,
  technologies,
  colors,
}: {
  view: string;
  technologies: ScoredTechnology[];
  colors: string[];
}) {
  const rows = getComparisonRows(view, technologies);

  return (
    <>
      {rows.map((row, index) => {
        const values = row.values;
        const allSame = values.every((v) => String(v) === String(values[0]));

        // Determine best value for color coding
        const bestValueIndex = getBestValueIndex(row.values, row.higherIsBetter);

        return (
          <tr key={index} className="border-b border-border-muted">
            <td className="px-4 py-3 text-sm text-foreground-secondary font-medium">{row.label}</td>
            {values.map((value, techIndex) => {
              const isBest = techIndex === bestValueIndex && !allSame;
              return (
                <td
                  key={techIndex}
                  className={`px-4 py-3 text-sm ${
                    isBest ? 'font-semibold' : ''
                  }`}
                  style={{ color: isBest ? colors[techIndex] : '#E8D5B7' }}
                >
                  {formatComparisonValue(value, isBest, allSame)}
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
}

function getComparisonRows(
  view: string,
  technologies: ScoredTechnology[]
): Array<{ label: string; values: any[]; higherIsBetter: boolean }> {
  switch (view) {
    case 'overview':
      return [
        { label: 'Description', values: technologies.map((t) => t.description), higherIsBetter: false },
        { label: 'Category', values: technologies.map((t) => t.category), higherIsBetter: false },
        { label: 'Website', values: technologies.map((t) => t.website), higherIsBetter: false },
        { label: 'License', values: technologies.map((t) => t.license), higherIsBetter: false },
        { label: 'Maturity', values: technologies.map((t) => t.technical.maturity), higherIsBetter: true },
        { label: 'Community Size', values: technologies.map((t) => t.ecosystem.communitySize), higherIsBetter: true },
        { label: 'Documentation', values: technologies.map((t) => t.ecosystem.documentation), higherIsBetter: true },
        { label: 'Overall Score', values: technologies.map((t) => Math.round(t.score)), higherIsBetter: true },
      ];

    case 'technical':
      return [
        { label: 'Languages', values: technologies.map((t) => t.technical.language.join(', ')), higherIsBetter: false },
        { label: 'Architecture', values: technologies.map((t) => t.technical.architecture.join(', ')), higherIsBetter: false },
        { label: 'Scalability', values: technologies.map((t) => t.technical.scalability), higherIsBetter: true },
        { label: 'Performance', values: technologies.map((t) => t.technical.performance), higherIsBetter: true },
        { label: 'Learning Curve', values: technologies.map((t) => t.technical.learningCurve), higherIsBetter: false },
        { label: 'Maturity', values: technologies.map((t) => t.technical.maturity), higherIsBetter: true },
        { label: 'Technical Score', values: technologies.map((t) => Math.round(t.scoreBreakdown.technical * 100)), higherIsBetter: true },
      ];

    case 'operational':
      return [
        { label: 'Hosting Options', values: technologies.map((t) => t.operations.hostingOptions.join(', ')), higherIsBetter: false },
        { label: 'Maintenance', values: technologies.map((t) => t.operations.maintenanceComplexity), higherIsBetter: false },
        { label: 'Monitoring', values: technologies.map((t) => t.operations.monitoringComplexity), higherIsBetter: false },
        { label: 'Backup/Recovery', values: technologies.map((t) => t.operations.backupRecovery), higherIsBetter: false },
        { label: 'Enterprise Support', values: technologies.map((t) => t.ecosystem.enterpriseSupport), higherIsBetter: true },
        { label: 'Operational Score', values: technologies.map((t) => Math.round(t.scoreBreakdown.operational * 100)), higherIsBetter: true },
      ];

    case 'cost':
      return [
        { label: 'Pricing Model', values: technologies.map((t) => t.pricing.model), higherIsBetter: false },
        { label: 'Starting Price', values: technologies.map((t) => t.pricing.startingPrice || 'Free'), higherIsBetter: false },
        { label: 'Free Option', values: technologies.map((t) => t.pricing.freeOption), higherIsBetter: true },
        { label: 'License Type', values: technologies.map((t) => t.license), higherIsBetter: false },
        { label: 'Vendor Lock-in', values: technologies.map((t) => t.migration.vendorLockIn), higherIsBetter: false },
        { label: 'Data Portability', values: technologies.map((t) => t.migration.dataPortability), higherIsBetter: true },
        { label: 'Cost Score', values: technologies.map((t) => Math.round(t.scoreBreakdown.cost * 100)), higherIsBetter: true },
      ];

    case 'compliance':
      return [
        { label: 'GDPR', values: technologies.map((t) => t.compliance.gdpr), higherIsBetter: true },
        { label: 'HIPAA', values: technologies.map((t) => t.compliance.hipaa), higherIsBetter: true },
        { label: 'SOC 2', values: technologies.map((t) => t.compliance.soc2), higherIsBetter: true },
        { label: 'PCI DSS', values: technologies.map((t) => t.compliance.pci), higherIsBetter: true },
        { label: 'Encryption', values: technologies.map((t) => t.compliance.security.encryption), higherIsBetter: true },
        { label: 'Audit Logs', values: technologies.map((t) => t.compliance.security.auditLogs), higherIsBetter: true },
        { label: 'RBAC', values: technologies.map((t) => t.compliance.security.rbac), higherIsBetter: true },
        { label: 'MFA', values: technologies.map((t) => t.compliance.security.mfa), higherIsBetter: true },
        { label: 'Compliance Score', values: technologies.map((t) => Math.round(t.scoreBreakdown.compliance * 100)), higherIsBetter: true },
      ];

    default:
      return [];
  }
}

function getBestValueIndex(values: any[], higherIsBetter: boolean): number {
  if (values.length === 0) return -1;

  // For boolean values
  if (typeof values[0] === 'boolean') {
    return higherIsBetter ? values.indexOf(true) : values.indexOf(false);
  }

  // For numeric values
  if (typeof values[0] === 'number') {
    if (higherIsBetter) {
      const max = Math.max(...values);
      return values.indexOf(max);
    } else {
      const min = Math.min(...values);
      return values.indexOf(min);
    }
  }

  // For string values with known orderings
  const levelOrder = ['low', 'medium', 'high', 'very-high'];
  const complexityOrder = ['easy', 'moderate', 'steep', 'very-steep'];
  const maturityOrder = ['experimental', 'early', 'stable', 'mature', 'legacy'];
  const sizeOrder = ['small', 'medium', 'large', 'very-large'];
  const qualityOrder = ['poor', 'fair', 'good', 'excellent'];
  const lockInOrder = ['none', 'low', 'medium', 'high'];
  const portabilityOrder = ['difficult', 'moderate', 'easy'];

  const stringValues = values.map((v) => String(v).toLowerCase());

  if (stringValues.every((v) => levelOrder.includes(v))) {
    const indices = stringValues.map((v) => levelOrder.indexOf(v));
    return higherIsBetter ? indices.indexOf(Math.max(...indices)) : indices.indexOf(Math.min(...indices));
  }

  if (stringValues.every((v) => complexityOrder.includes(v))) {
    const indices = stringValues.map((v) => complexityOrder.indexOf(v));
    return indices.indexOf(Math.min(...indices)); // Lower complexity is better
  }

  if (stringValues.every((v) => maturityOrder.includes(v))) {
    const indices = stringValues.map((v) => maturityOrder.indexOf(v));
    return indices.indexOf(Math.max(...indices)); // Higher maturity is better
  }

  if (stringValues.every((v) => sizeOrder.includes(v))) {
    const indices = stringValues.map((v) => sizeOrder.indexOf(v));
    return indices.indexOf(Math.max(...indices)); // Larger community is better
  }

  if (stringValues.every((v) => qualityOrder.includes(v))) {
    const indices = stringValues.map((v) => qualityOrder.indexOf(v));
    return indices.indexOf(Math.max(...indices)); // Better quality is better
  }

  if (stringValues.every((v) => lockInOrder.includes(v))) {
    const indices = stringValues.map((v) => lockInOrder.indexOf(v));
    return indices.indexOf(Math.min(...indices)); // Less lock-in is better
  }

  if (stringValues.every((v) => portabilityOrder.includes(v))) {
    const indices = stringValues.map((v) => portabilityOrder.indexOf(v));
    return indices.indexOf(Math.max(...indices)); // Easier portability is better
  }

  return -1; // Can't determine best
}

function formatComparisonValue(value: any, isBest: boolean, allSame: boolean): React.ReactNode {
  if (value === null || value === undefined) return '-';

  if (typeof value === 'boolean') {
    const icon = value ? '✓' : '✗';
    return icon;
  }

  if (typeof value === 'string' && value.startsWith('http')) {
    return (
      <a href={value} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
        Visit Site
      </a>
    );
  }

  return String(value);
}

function getRadarChartData(technologies: ScoredTechnology[]) {
  const dimensions = ['Compliance', 'Technical', 'Operational', 'Cost', 'Ecosystem', 'Strategic'];
  const dimensionKeys: (keyof ScoredTechnology['scoreBreakdown'])[] = [
    'compliance',
    'technical',
    'operational',
    'cost',
    'ecosystem',
    'strategic',
  ];

  return dimensions.map((label, index) => {
    const point: any = { dimension: label };
    technologies.forEach((tech) => {
      point[tech.id] = Math.round(tech.scoreBreakdown[dimensionKeys[index]] * 100);
    });
    return point;
  });
}

interface DecisionGuide {
  technology: string;
  when: string[];
  reasons: string[];
}

function generateDecisionGuide(technologies: ScoredTechnology[]): DecisionGuide[] {
  // Calculate relative strengths by comparing technologies
  const guides = technologies.map((tech, index) => {
    const when: string[] = [];
    const reasons: string[] = [];
    const others = technologies.filter((_, i) => i !== index);

    // Compare scores to find this technology's advantages
    const scoreAdvantages: Array<{ dimension: string; advantage: number }> = [];
    const dimensions: Array<keyof ScoredTechnology['scoreBreakdown']> = [
      'compliance', 'technical', 'operational', 'cost', 'ecosystem', 'strategic'
    ];

    dimensions.forEach(dim => {
      const myScore = tech.scoreBreakdown[dim];
      const avgOthers = others.reduce((sum, t) => sum + t.scoreBreakdown[dim], 0) / others.length;
      const advantage = myScore - avgOthers;
      if (advantage > 0.1) { // Significant advantage
        scoreAdvantages.push({ dimension: dim, advantage });
      }
    });

    // Sort by biggest advantages
    scoreAdvantages.sort((a, b) => b.advantage - a.advantage);

    // Generate differentiated criteria based on advantages
    scoreAdvantages.slice(0, 3).forEach(({ dimension }) => {
      switch (dimension) {
        case 'compliance':
          if (tech.compliance.hipaa && !others.some(t => t.compliance.hipaa)) {
            when.push('HIPAA compliance is required (healthcare data)');
          } else if (tech.compliance.pci && !others.some(t => t.compliance.pci)) {
            when.push('PCI DSS compliance needed (payment processing)');
          } else if (Object.values(tech.compliance).filter(Boolean).length > Object.values(others[0].compliance).filter(Boolean).length) {
            when.push('Maximum compliance coverage across regulations');
          }
          break;
        case 'technical':
          if (tech.technical.performance === 'very-high' && others.some(t => t.technical.performance !== 'very-high')) {
            when.push(`Highest performance tier needed (${tech.technical.performance})`);
          } else if (tech.technical.scalability === 'very-high' && others.some(t => t.technical.scalability !== 'very-high')) {
            when.push(`Maximum scalability required (${tech.technical.scalability})`);
          } else if (tech.technical.maturity === 'mature' && others.some(t => t.technical.maturity !== 'mature')) {
            when.push('Prefer most mature, battle-tested option');
          }
          break;
        case 'cost':
          if (tech.license === 'open-source' && others.some(t => t.license !== 'open-source')) {
            when.push('Open-source preferred to minimize licensing costs');
          } else if (tech.pricing.freeOption && !others.some(t => t.pricing.freeOption)) {
            when.push('Need free tier or zero upfront cost');
          } else if (tech.pricing.model === 'free') {
            when.push('100% free solution is essential');
          }
          break;
        case 'ecosystem':
          if (tech.ecosystem.communitySize === 'very-large' && others.some(t => t.ecosystem.communitySize !== 'very-large')) {
            when.push('Largest community and most resources available');
          } else if (tech.ecosystem.documentation === 'excellent' && others.some(t => t.ecosystem.documentation !== 'excellent')) {
            when.push('Best-in-class documentation is critical');
          } else if (tech.ecosystem.enterpriseSupport && !others.some(t => t.ecosystem.enterpriseSupport)) {
            when.push('Enterprise support required');
          }
          break;
        case 'operational':
          if (tech.operations.maintenanceComplexity === 'low' && others.some(t => t.operations.maintenanceComplexity !== 'low')) {
            when.push('Minimize operational overhead and maintenance');
          } else if (tech.operations.hostingOptions.includes('managed') && !others.some(t => t.operations.hostingOptions.includes('managed'))) {
            when.push('Prefer fully managed service');
          }
          break;
        case 'strategic':
          if (tech.migration.vendorLockIn === 'none' && others.some(t => t.migration.vendorLockIn !== 'none')) {
            when.push('Zero vendor lock-in is priority');
          } else if (tech.migration.dataPortability === 'easy' && others.some(t => t.migration.dataPortability !== 'easy')) {
            when.push('Easy data portability and migration');
          }
          break;
      }
    });

    // Add unique use cases (not shared with others)
    const uniqueUseCases = tech.suitability.whenToUse.filter(
      useCase => !others.some(other => other.suitability.whenToUse.includes(useCase))
    );
    when.push(...uniqueUseCases.slice(0, 2));

    // Add unique strengths
    const uniqueStrengths = tech.suitability.strengths.filter(
      strength => !others.some(other => other.suitability.strengths.includes(strength))
    );
    reasons.push(...uniqueStrengths.slice(0, 2));

    // Add shared strengths only if this tech excels at them
    reasons.push(...tech.suitability.strengths.slice(0, 3));

    // Add specific integrations that differentiate
    const uniqueIntegrations = tech.ecosystem.integrations.filter(
      integration => !others.some(other => other.ecosystem.integrations.includes(integration))
    );
    if (uniqueIntegrations.length > 0) {
      reasons.push(`Unique integrations: ${uniqueIntegrations.slice(0, 2).join(', ')}`);
    }

    return {
      technology: tech.name,
      when: [...new Set(when)].slice(0, 5), // Deduplicate and limit
      reasons: [...new Set(reasons)].slice(0, 4), // Deduplicate and limit
    };
  });

  return guides;
}

function exportToMarkdown(technologies: ScoredTechnology[], decisionGuide: DecisionGuide[]) {
  let markdown = `# Technology Comparison: ${technologies.map((t) => t.name).join(' vs ')}\n\n`;
  markdown += `Generated on ${new Date().toLocaleDateString()}\n\n`;

  // Overview table
  markdown += `## Overview\n\n`;
  markdown += `| Criteria | ${technologies.map((t) => t.name).join(' | ')} |\n`;
  markdown += `|----------|${technologies.map(() => '----------').join('|')}|\n`;
  markdown += `| Overall Score | ${technologies.map((t) => `${t.score.toFixed(1)}/100`).join(' | ')} |\n`;
  markdown += `| License | ${technologies.map((t) => t.license).join(' | ')} |\n`;
  markdown += `| Pricing | ${technologies.map((t) => t.pricing.model).join(' | ')} |\n`;
  markdown += `| Maturity | ${technologies.map((t) => t.technical.maturity).join(' | ')} |\n\n`;

  // Score breakdown
  markdown += `## Score Breakdown\n\n`;
  markdown += `| Dimension | ${technologies.map((t) => t.name).join(' | ')} |\n`;
  markdown += `|-----------|${technologies.map(() => '----------').join('|')}|\n`;
  ['compliance', 'technical', 'operational', 'cost', 'ecosystem', 'strategic'].forEach((dim) => {
    const key = dim as keyof ScoredTechnology['scoreBreakdown'];
    markdown += `| ${dim.charAt(0).toUpperCase() + dim.slice(1)} | ${technologies
      .map((t) => Math.round(t.scoreBreakdown[key] * 100))
      .join(' | ')} |\n`;
  });
  markdown += `\n`;

  // Decision guide
  markdown += `## Decision Guide\n\n`;
  decisionGuide.forEach((guide, index) => {
    markdown += `### Choose ${guide.technology} if:\n\n`;
    guide.when.forEach((reason) => {
      markdown += `- ${reason}\n`;
    });
    markdown += `\n**Key Strengths:**\n\n`;
    guide.reasons.forEach((reason) => {
      markdown += `- ${reason}\n`;
    });
    markdown += `\n`;
  });

  // Download
  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `tech-comparison-${technologies.map((t) => t.name.toLowerCase()).join('-')}.md`;
  a.click();
  URL.revokeObjectURL(url);
}
